import type {
  ProjectMetadataFieldConfig,
  ProjectMetadataSchema,
  StoryMetadataValue,
  StoryMetadataValues,
} from "@theirstory/common/api_types/backend/metadata";
import { isLockedBaseMetadataFieldKey } from "@theirstory/common/api_types/backend/metadata";

export interface InterviewMetadataState {
  schema: ProjectMetadataSchema;
  values: StoryMetadataValues;
  source: string;
  projectId: string;
  isLegacy?: boolean;
}

export function getEnabledMetadataFields(
  schema: ProjectMetadataSchema,
  values?: StoryMetadataValues
): ProjectMetadataFieldConfig[] {
  return [...schema.fields]
    .filter((field) => {
      if (field.key === "creator") return false;
      if (field.deleted) {
        return !!values && !isStoryMetadataValueEmpty(values[field.key]);
      }
      if (!field.enabled) return false;
      return true;
    })
    .sort((a, b) => a.order - b.order);
}

/** Deleted fields that had values when edit opened; kept in the form until reload. */
export function getSessionDeletedMetadataFieldKeys(
  schema: ProjectMetadataSchema,
  values: StoryMetadataValues
): Set<string> {
  return new Set(
    schema.fields
      .filter((field) => field.deleted && !isStoryMetadataValueEmpty(values[field.key]))
      .map((field) => field.key)
  );
}

/** Edit-form fields: active schema fields plus session-pinned deleted fields. */
export function getEditableMetadataFields(
  schema: ProjectMetadataSchema,
  values: StoryMetadataValues,
  sessionDeletedFieldKeys: ReadonlySet<string>
): ProjectMetadataFieldConfig[] {
  const fields = getEnabledMetadataFields(schema, values);
  const keys = new Set(fields.map((field) => field.key));

  for (const field of schema.fields) {
    if (field.deleted && sessionDeletedFieldKeys.has(field.key) && !keys.has(field.key)) {
      fields.push(field);
      keys.add(field.key);
    }
  }

  return fields.sort((a, b) => a.order - b.order);
}

export function extractCustomMetadataValues(
  schema: ProjectMetadataSchema,
  values: StoryMetadataValues
): StoryMetadataValues {
  const customValues: StoryMetadataValues = {};

  for (const field of getEnabledMetadataFields(schema, values)) {
    if (!isLockedBaseMetadataFieldKey(field.key)) {
      customValues[field.key] = values[field.key] ?? null;
    }
  }

  return customValues;
}

export function isArrayMetadataFieldKey(key: string): boolean {
  return key === "participants" || key === "tags";
}

export function isMultiSelectMetadataField(field: Pick<ProjectMetadataFieldConfig, "key" | "answerType">): boolean {
  return field.answerType === "Multi-Select" || isArrayMetadataFieldKey(field.key);
}

export function getMetadataArrayDisplayValues(value: StoryMetadataValue | undefined): string[] {
  if (Array.isArray(value)) {
    return value.map((entry) => (typeof entry === "string" ? entry.trim() : String(entry).trim())).filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return [String(value)];
  }

  return [];
}

function normalizeMultiSelectFormValue(raw: string | string[] | undefined): string[] {
  if (!Array.isArray(raw)) {
    if (typeof raw === "string") {
      const trimmed = raw.trim();
      return trimmed ? [trimmed] : [];
    }
    return [];
  }

  return raw.map((entry) => entry.trim()).filter(Boolean);
}

function formatDateForForm(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  return parsed.toISOString().slice(0, 10);
}

function formatDateForStorage(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = new Date(trimmed.includes("T") ? trimmed : `${trimmed}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString();
}

export function storyMetadataValueToFormValue(
  key: string,
  value: StoryMetadataValue | undefined,
  field?: ProjectMetadataFieldConfig
): string | string[] {
  if (field && isMultiSelectMetadataField(field)) {
    return getMetadataArrayDisplayValues(value);
  }

  if (isArrayMetadataFieldKey(key)) {
    return Array.isArray(value) ? value : [];
  }

  if (value == null) {
    return "";
  }

  const isDateField = key === "date" || field?.answerType === "Date";

  if (isDateField && typeof value === "string") {
    return formatDateForForm(value);
  }

  return String(value);
}

export function buildFormDefaultValues(
  schema: ProjectMetadataSchema,
  values: StoryMetadataValues,
  fields?: ProjectMetadataFieldConfig[]
): Record<string, string | string[]> {
  const defaults: Record<string, string | string[]> = {};
  const fieldsToUse = fields ?? getEnabledMetadataFields(schema, values);

  for (const field of fieldsToUse) {
    defaults[field.key] = storyMetadataValueToFormValue(field.key, values[field.key], field);
  }

  return defaults;
}

export function buildMetadataValuesFromForm(
  schema: ProjectMetadataSchema,
  formValues: Record<string, string | string[]>,
  preservedValues?: StoryMetadataValues,
  fields?: ProjectMetadataFieldConfig[]
): StoryMetadataValues {
  const result: StoryMetadataValues = {};
  const fieldsToUse = fields ?? getEnabledMetadataFields(schema, preservedValues);

  for (const field of fieldsToUse) {
    const raw = formValues[field.key];

    if (isMultiSelectMetadataField(field)) {
      result[field.key] = normalizeMultiSelectFormValue(raw);
      continue;
    }

    if (field.answerType === "Date" || field.key === "date") {
      const text = typeof raw === "string" ? raw : "";
      result[field.key] = formatDateForStorage(text);
      continue;
    }

    const text = typeof raw === "string" ? raw.trim() : "";
    result[field.key] = text || null;
  }

  return result;
}

export function metadataValuesToLegacyConcludeFields(values: StoryMetadataValues) {
  return {
    title: typeof values.title === "string" ? values.title : "",
    description: typeof values.description === "string" ? values.description : "",
    participants: Array.isArray(values.participants) ? values.participants : [],
    tags: Array.isArray(values.tags) ? values.tags : [],
  };
}

export function isStoryMetadataValueEmpty(value: StoryMetadataValue | undefined): boolean {
  if (value == null) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every((entry) => typeof entry !== "string" || entry.trim() === "");
  }

  return typeof value === "string" && value.trim() === "";
}

// Already shown in the Hub header (title, date, author), so the metadata
// section below it should not repeat them.
const HUB_HEADER_FIELD_KEYS = new Set(["title", "date", "creator"]);

export function getMetadataFieldsForDisplay(
  schema: ProjectMetadataSchema,
  values: StoryMetadataValues
): ProjectMetadataFieldConfig[] {
  const fields = getEnabledMetadataFields(schema, values).filter((field) => !HUB_HEADER_FIELD_KEYS.has(field.key));

  if (schema.showEmptyFields) {
    return fields;
  }

  return fields.filter((field) => !isStoryMetadataValueEmpty(values[field.key]));
}

export function getMetadataStringDisplayValue(
  key: string,
  value: StoryMetadataValue | undefined,
  field?: ProjectMetadataFieldConfig
): string {
  if (field && isMultiSelectMetadataField(field)) {
    return getMetadataArrayDisplayValues(value).join(", ");
  }

  if (isArrayMetadataFieldKey(key)) {
    return getMetadataArrayDisplayValues(value).join(", ");
  }

  if (isStoryMetadataValueEmpty(value)) {
    return "";
  }

  return storyMetadataValueToFormValue(key, value, field) as string;
}
